import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Ban, Building2, ChevronDown, ClipboardSignature, Clock, FileCheck, FileSignature, FileText,
  House, IdCard, Info, Landmark, ListChecks, MapPin, MessageSquareText, Phone, Receipt, ShieldCheck, Users,
} from 'lucide-react';
import { publicCoverageApi } from '../../services/publicApi';
import { flagCode } from '../../utils/flags';
import Seo from '../../components/Seo';
import NotFound from '../../components/NotFound';
import { countryPath, dialCodeFor, kindsOf, KIND_INFO, listJoin } from '../../utils/countryPage';
import { groupColor } from '../../utils/numberGroups';
import {
  activationSummary, addressScope, addressSummary, commonToAll, distinctDocuments, linkParts, offeredTypes,
  regulationTabs, rowLabel, typeLabel, withoutCommon,
} from '../../utils/regulations';

const NAVY = '#0a1f2e';

const regTitle = (c) => [`${c} Phone Number Regulations & KYC`, `${c} Number Regulations`, `${c} Number KYC`]
  .find((t) => t.length <= 52) || `${c} Number KYC`;

// Document type (services/number_identity.py RULE_DOCS) -> icon and how the customer provides it.
const DOC_ICONS = {
  business_registration: Building2, id_document: IdCard, address_proof: House, letter_of_intent: FileSignature,
  letter_of_authorisation: FileSignature, tax_document: Receipt, usage_description: MessageSquareText,
  carrier_form: ClipboardSignature, regulator_document: Landmark, company_details: Building2,
};
const DOC_HINT = {
  text: 'You type this in when you order',
  us: 'We send you this form after you order',
  auto: 'Taken from your company details',
};
const END_USERS = {
  business: { title: 'Businesses only', body: 'Numbers are issued to companies, not private individuals.' },
  any: { title: 'Businesses and individuals', body: 'Companies and private individuals can both hold these numbers.' },
};

const Linked = ({ text }) => (
  <>
    {linkParts(text).map((p, i) => (p.url ? (
      <a key={i} href={p.url} target="_blank" rel="noopener noreferrer nofollow" className="underline underline-offset-2 break-all text-[#0a1f2e] hover:text-blue-700">
        {p.url.replace(/^https?:\/\/(www\.)?/, '').split(/[/?#]/)[0]}
      </a>
    ) : <React.Fragment key={i}>{p.text}</React.Fragment>))}
  </>
);

const Bullets = ({ items, icon: Icon = null, className = '' }) => (
  <ul className={`space-y-2 ${className}`}>
    {items.map((d) => (
      <li key={d.text || d} className="flex gap-2.5 text-gray-700 leading-relaxed">
        {Icon ? <Icon className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" aria-hidden="true" />
          : <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" aria-hidden="true" />}
        <div className="min-w-0">
          <Linked text={d.text || d} />
          {d.items?.length > 0 && (
            <ul className="mt-2 space-y-1.5 border-l-2 border-gray-200 pl-3">
              {d.items.map((x) => <li key={x} className="text-gray-600"><Linked text={x} /></li>)}
            </ul>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const Block = ({ icon: Icon, title, children }) => (
  <div>
    <h4 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
      <Icon className="w-4 h-4" aria-hidden="true" />{title}
    </h4>
    {children}
  </div>
);

const Documents = ({ row }) => (
  row.documents?.length ? (
    <ul className="space-y-2">
      {row.documents.map((label, i) => {
        const spec = row.document_types?.[i] || {};
        const Icon = DOC_ICONS[spec.type] || FileText;
        return (
          <li key={label} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#0a1f2e]/5">
              <Icon className="w-4 h-4" style={{ color: NAVY }} aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block text-gray-800">{label}</span>
              {DOC_HINT[spec.input] && <span className="block text-xs text-gray-500 mt-0.5">{DOC_HINT[spec.input]}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  ) : <p className="text-gray-600">{row.to_follow ? 'To be confirmed: we tell you exactly what is needed when you order.' : 'No documents listed.'}</p>
);

const RowDetail = ({ row, country, showLabel }) => {
  const scope = addressScope(row.address_scope, country);
  const who = END_USERS[row.end_users];
  return (
    <div className={showLabel ? 'rounded-xl border border-gray-200 p-4 sm:p-6' : ''}>
      {showLabel && <h4 className="text-lg font-bold mb-5" style={{ color: NAVY }}>{rowLabel(row)} numbers</h4>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-7">
          <Block icon={Users} title="Who can order">
            {who ? (
              <>
                <p className="font-semibold text-gray-900">{who.title}</p>
                <p className="text-gray-600 text-sm mt-1">{who.body}</p>
              </>
            ) : <p className="text-gray-700">No end-user restriction listed.</p>}
          </Block>
          <Block icon={MapPin} title="Address">
            <p className="font-semibold text-gray-900">
              {row.address_scope === 'ask' || !row.address ? scope.long : row.address}
            </p>
            {row.location?.length > 0 && <Bullets items={row.location} className="mt-2 text-sm" />}
          </Block>
          {(row.facts?.length > 0 || row.service_notes?.length > 0) && (
            <Block icon={Phone} title="Service details">
              {row.facts?.length > 0 && (
                <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
                  {row.facts.map((f) => (
                    <div key={f.label} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 px-3 py-2">
                      <dt className="text-gray-500">{f.label}</dt>
                      <dd className="text-gray-900 break-words">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {row.service_notes?.length > 0 && <Bullets items={row.service_notes} className="mt-3 text-sm" />}
            </Block>
          )}
        </div>
        <div className="space-y-7">
          <Block icon={FileCheck} title="Documents">
            <Documents row={row} />
          </Block>
          {row.rules?.length > 0 && (
            <Block icon={Ban} title="Restrictions">
              <Bullets items={row.rules} />
            </Block>
          )}
        </div>
      </div>
      {row.details?.length > 0 && (
        <details className="group mt-8 rounded-lg bg-gray-50 border border-gray-200">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 font-medium text-gray-800 [&::-webkit-details-marker]:hidden">
            <span className="flex items-center gap-2"><ListChecks className="w-4 h-4 text-gray-500" aria-hidden="true" />Full wording from the regulations</span>
            <ChevronDown className="w-4 h-4 text-gray-500 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="px-4 pb-4 text-sm"><Bullets items={row.details} /></div>
        </details>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, detail, children, className = '' }) => (
  <div className={`rounded-xl border border-gray-200 bg-white p-4 sm:p-5 min-w-0 ${className}`}>
    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
      <Icon className="w-4 h-4" aria-hidden="true" />{label}
    </p>
    {children || <p className="mt-2 text-lg sm:text-xl font-bold" style={{ color: NAVY }}>{value}</p>}
    {Array.isArray(detail) ? (
      <ul className="mt-1 space-y-0.5 text-sm text-gray-600">
        {detail.map((d) => <li key={`${d.label}-${d.value}`}><span className="font-medium text-gray-700">{d.label}:</span> {d.value}</li>)}
      </ul>
    ) : detail && <p className="mt-1 text-sm text-gray-600">{detail}</p>}
  </div>
);

const TypeChip = ({ type, muted }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${muted ? 'bg-gray-50 text-gray-400 ring-gray-200' : groupColor(type).badge}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${muted ? 'bg-gray-300' : groupColor(type).dot}`} aria-hidden="true" />
    {typeLabel(type)}
  </span>
);

const RegulatoryRequirements = () => {
  const { country } = useParams();
  const urlCountry = (() => { try { return decodeURIComponent(country); } catch (e) { return country; } })();

  const [result, setResult] = useState({ key: null });
  const [active, setActive] = useState(null);
  const loading = result.key !== urlCountry;
  const raw = loading ? null : result.data;
  // The one public spelling of the country name (other spellings redirect to it).
  const name = raw?.country || urlCountry;
  const countryCode = flagCode(name);

  useEffect(() => {
    let live = true;
    publicCoverageApi.country(urlCountry)
      .then((data) => { if (live) setResult({ key: urlCountry, data }); })
      .catch((error) => {
        console.error('Failed to fetch country data:', error);
        if (live) setResult({ key: urlCountry, data: null });
      });
    return () => { live = false; };
  }, [urlCountry]);

  const path = countryPath(name, '/regulatory-requirements');
  const kinds = kindsOf(raw);
  const dial = dialCodeFor(name);
  const description = raw && kinds.length
    ? `Documents, address rules and KYC checks for ${listJoin(kinds.filter((k) => k !== 'uc').map((k) => KIND_INFO[k].plural.replace(' numbers', '')))} numbers in ${name}${dial ? ` (${dial})` : ''}, and how VOIP2 collects them.`
    : `Documents, address rules and KYC checks needed for phone numbers in ${name}, and how VOIP2 collects them.`;
  const seo = (
    <Seo
      title={regTitle(name)}
      description={description.length <= 155 ? description : `Documents, address rules and KYC checks needed for phone numbers in ${name}.`}
      path={path}
      breadcrumbs={[{ name: 'Coverage', path: '/coverage' }, { name, path: countryPath(name) },
        { name: 'Regulatory requirements', path }]}
    />
  );

  if (raw?.country && raw.country !== urlCountry) {
    return <Navigate to={path} replace />;
  }
  if (!loading && !raw) return <NotFound />;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        {seo}
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0a1f2e] mx-auto mb-4" />
          <p className="text-gray-600">Loading regulatory requirements...</p>
        </div>
      </div>
    );
  }

  const tabs = regulationTabs(raw);
  const offered = offeredTypes(raw);
  const usedRows = [...new Set(tabs.flatMap((t) => t.rows))];
  const common = commonToAll(usedRows);
  const hasCommon = common.rules.length > 0 || common.details.length > 0;
  const current = tabs.find((t) => t.id === active) || tabs[0];
  const address = addressSummary(tabs.filter((t) => t.rows.length), name);
  const docs = distinctDocuments(usedRows);
  const activation = activationSummary(raw, tabs);
  const anyRules = usedRows.length > 0;

  return (
    <div className="min-h-screen pt-24 pb-16 overflow-x-hidden">
      {seo}
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="px-4 sm:px-6 lg:px-8 mb-8">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <Link to="/" className="hover:text-[#0a1f2e] underline-offset-2 hover:underline">Home</Link>
          <span aria-hidden="true">/</span>
          <Link to="/coverage" className="hover:text-[#0a1f2e] underline-offset-2 hover:underline">Coverage</Link>
          <span aria-hidden="true">/</span>
          <Link to={countryPath(name)} className="hover:text-[#0a1f2e] underline-offset-2 hover:underline">{name}</Link>
          <span aria-hidden="true">/</span>
          <span className="text-gray-900 font-medium">Regulatory requirements</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <Link to={countryPath(name)} className="inline-flex items-center text-[#c8ff00] hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
            {name} phone numbers
          </Link>
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
            {countryCode && (
              <img
                src={`https://flagcdn.com/w160/${countryCode}.png`}
                alt={`${name} flag`}
                width="96" height="72"
                className="w-20 h-[60px] sm:w-24 sm:h-[72px] rounded-lg shadow-2xl border-4 border-white object-cover flex-shrink-0"
              />
            )}
            <div className="min-w-0">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
                {name} Phone Number Regulatory Requirements
              </h1>
              <p className="mt-4 text-lg text-gray-300 max-w-3xl">
                {dial && <span className="font-semibold text-[#c8ff00] mr-2">{dial}</span>}
                What you need to provide before {name} numbers are activated, by number type.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section className="px-4 sm:px-6 lg:px-8 -mt-px bg-gray-50 border-b border-gray-200 py-8 mb-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="sr-only">At a glance</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <SummaryCard className="col-span-2 lg:col-span-1" icon={Phone} label="Number types" detail={offered.length ? null : 'None on offer right now'}>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {['local', 'national', 'mobile', '2way', 'tollfree', 'uifn'].map((t) => (
                  <TypeChip key={t} type={t} muted={!offered.includes(t)} />
                ))}
              </div>
            </SummaryCard>
            <SummaryCard icon={MapPin} label="Address required" value={address ? address.short : 'None listed'} detail={address?.detail} />
            <SummaryCard
              icon={FileCheck} label="Documents needed"
              value={docs.length ? `${docs.length} ${docs.length === 1 ? 'type' : 'types'}` : 'None listed'}
              detail={docs.length ? (tabs.filter((t) => t.rows.length).length > 1 ? 'Across all number types' : null) : null}
            />
            <SummaryCard
              icon={Clock} label="Typical activation"
              value={activation ? activation.short : 'On request'}
              detail={activation ? activation.detail : 'We confirm the timing when you order'}
            />
          </div>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          {/* Shared requirements */}
          {hasCommon && tabs.length > 1 && (
            <section aria-labelledby="all-types">
              <h2 id="all-types" className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: NAVY }}>Applies to all number types</h2>
              <p className="text-gray-600 mb-6">These points are the same for every {name} number type, so they are not repeated below.</p>
              <div className="rounded-xl border-l-4 border-[#c8ff00] bg-white shadow-sm ring-1 ring-gray-200 p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
                {common.rules.length > 0 && (
                  <Block icon={Ban} title="Restrictions"><Bullets items={common.rules} /></Block>
                )}
                {common.details.length > 0 && (
                  <Block icon={Info} title="From the regulations"><Bullets items={common.details} className="text-sm" /></Block>
                )}
              </div>
            </section>
          )}

          {/* Per number type */}
          <section aria-labelledby="by-type">
            <h2 id="by-type" className="text-2xl sm:text-3xl font-bold mb-6" style={{ color: NAVY }}>Requirements by number type</h2>
            {!anyRules ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-6 sm:p-8 flex flex-col sm:flex-row gap-4">
                <ShieldCheck className="w-10 h-10 flex-shrink-0" style={{ color: NAVY }} aria-hidden="true" />
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: NAVY }}>No specific requirements listed for {name}</h3>
                  <p className="text-gray-700">
                    The regulations list has no special documents or address rules for {name} numbers at the moment.
                    We confirm anything that is needed when you order.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {tabs.length > 1 && (
                  <div role="tablist" aria-label="Number types" className="flex flex-wrap gap-2 mb-6">
                    {tabs.map((t) => {
                      const on = t.id === current.id;
                      return (
                        <button
                          key={t.id} id={`tab-${t.id}`} type="button" role="tab" aria-selected={on} aria-controls={`panel-${t.id}`}
                          onClick={() => setActive(t.id)}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${on
                            ? 'bg-[#0a1f2e] text-white shadow' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:ring-[#0a1f2e]'}`}
                        >
                          <span className="flex -space-x-1" aria-hidden="true">
                            {t.keys.map((k) => <span key={k} className={`h-2 w-2 rounded-full ring-1 ring-white ${groupColor(k).dot}`} />)}
                          </span>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {tabs.map((t) => (
                  <div
                    key={t.id} id={`panel-${t.id}`} role={tabs.length > 1 ? 'tabpanel' : undefined}
                    aria-labelledby={tabs.length > 1 ? `tab-${t.id}` : undefined} hidden={t.id !== current.id}
                    className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 p-5 sm:p-8"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                      <h3 className="text-xl sm:text-2xl font-bold" style={{ color: NAVY }}>{t.label} numbers</h3>
                      {activation?.byType && t.keys.some((k) => activation.byType[k]) && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                          <Clock className="w-4 h-4" aria-hidden="true" />
                          Activation: {activation.byType[t.keys.find((k) => activation.byType[k])]}
                        </span>
                      )}
                    </div>
                    {t.rows.length ? (
                      <div className="space-y-6">
                        {t.rows.map((r) => (
                          <RowDetail key={`${r.product}-${t.id}`} row={withoutCommon(r, common)} country={name} showLabel={t.rows.length > 1} />
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-700">
                        No specific requirements are listed for {t.label.toLowerCase()} numbers in {name}. We confirm anything that is needed when you order.
                      </p>
                    )}
                  </div>
                ))}
              </>
            )}
          </section>

          {/* How we collect */}
          {anyRules && (
            <section aria-labelledby="how" className="rounded-xl bg-gray-50 border border-gray-200 p-5 sm:p-6 flex gap-4">
              <Info className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: NAVY }} aria-hidden="true" />
              <div>
                <h2 id="how" className="text-lg font-bold mb-1" style={{ color: NAVY }}>How we collect these documents</h2>
                <p className="text-gray-700">
                  After you order, you upload documents once through Company verification (KYC) in the customer portal; they are reused for later orders.
                  We may ask for more information at any stage of the registration process.
                </p>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="rounded-2xl bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] p-6 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Ready to order {name} numbers?</h2>
              <p className="text-gray-300 mt-2">See the number types and activation times, or ask us about the requirements.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to={countryPath(name)}
                className="inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#c8ff00', color: NAVY }}
              >
                Order {name} numbers <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center rounded-lg px-6 py-3 font-semibold text-white ring-1 ring-white/40 hover:bg-white/10">
                Talk to us
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RegulatoryRequirements;
